import React, { useEffect, useMemo, useState } from "react";
import { api, formatError } from "../lib/api";
import { toast } from "sonner";
import { Phone, CheckCircle2, ArrowRight, RotateCcw, ShieldCheck } from "lucide-react";

const E164_RE = /^\+[1-9]\d{6,14}$/;

// Small country picker exposed only to testers. Real founders never
// see this control — they get the same US-only 10-digit input the
// Sign Up page uses. Add rows as new tester regions come online.
const TESTER_COUNTRY_OPTIONS = [
  { code: "+91", label: "🇮🇳 India (+91)" },
  { code: "+1",  label: "🇺🇸 US / Canada (+1)" },
  { code: "+44", label: "🇬🇧 UK (+44)" },
];

// US 10-digit auto-formatter — mirror of Sign Up's `onPhoneChange`
// so tester + real user share the exact same typing UX for +1.
function formatUsNational(raw) {
  let digits = String(raw || "").replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1);
  digits = digits.slice(0, 10);
  if (digits.length > 6) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  if (digits.length > 3) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return digits;
}

/**
 * SMS phone verification. Shows "Verified" state if the user already has one.
 * Otherwise runs a 2-step flow: enter number → SMS code → confirm. Once
 * verified, the number gates email/password changes via SecureFieldPanel.
 *
 * Country handling
 * ----------------
 * The Sign Up page hard-codes `+1` (US) since every real founder today
 * is US-based. This panel mirrors that behaviour for real founders so
 * the Account page and Sign Up page feel like one flow.
 *
 * Internal testers (role="tester") sit outside that assumption — most
 * are in India, some in the UK. For testers only we expose a small
 * country-code picker next to the national-number field so they can
 * verify their real number without SMS routing to the wrong country.
 * Real founders never see the picker.
 */
export default function PhoneVerifyPanel({ user, onSaved }) {
  const isTester = user?.role === "tester";

  const [step, setStep] = useState("idle");    // idle | verify | replacing
  // Country code + national number are tracked separately in state so
  // the tester picker can flip between +91 / +1 / etc. without losing
  // the digits the tester just typed. Real founders always stay on +1.
  const [countryCode, setCountryCode] = useState(isTester ? "+91" : "+1");
  const [national, setNational] = useState("");
  const [code, setCode] = useState("");
  const [sentTo, setSentTo] = useState("");
  const [busy, setBusy] = useState(false);

  // Seed the fields from the user's stored E.164 whenever it changes
  // (e.g. after successful verification the outer page reloads /me).
  useEffect(() => {
    const stored = user?.phone_e164 || "";
    if (!stored) { setCountryCode(isTester ? "+91" : "+1"); setNational(""); return; }
    // Match the longest known dialing code first so "+1" doesn't win
    // against "+91". Falls back to peeling off the leading "+" digit.
    const opts = [...TESTER_COUNTRY_OPTIONS].sort((a, b) => b.code.length - a.code.length);
    const hit = opts.find((o) => stored.startsWith(o.code));
    const cc = hit?.code || (stored.match(/^\+\d{1,3}/)?.[0] || "+1");
    const rest = stored.slice(cc.length);
    setCountryCode(cc);
    setNational(cc === "+1" ? formatUsNational(rest) : rest.replace(/\D/g, ""));
  }, [user?.phone_e164, isTester]);

  const verified = !!user?.phone_verified;
  const nationalDigits = useMemo(() => national.replace(/\D/g, ""), [national]);

  // For +1 we enforce the same "exactly 10 digits" rule as Sign Up.
  // For every other dialing code (tester-only today) accept 7-14 digits
  // so an Indian mobile (10 digits after +91) or a UK mobile (10 digits
  // after +44) both validate cleanly without a per-country pattern.
  const numberOk = countryCode === "+1"
    ? nationalDigits.length === 10
    : nationalDigits.length >= 7 && nationalDigits.length <= 14;

  const e164 = `${countryCode}${nationalDigits}`;
  const e164Ok = E164_RE.test(e164) && numberOk;

  const reset = () => { setStep("idle"); setCode(""); setSentTo(""); };

  const onNationalChange = (raw) => {
    // Reuse the Sign Up formatter only when we're on +1. All other
    // countries (tester picker) get a plain-digits input to avoid
    // baking a US-specific mask into an international number.
    if (countryCode === "+1") setNational(formatUsNational(raw));
    else setNational(String(raw).replace(/\D/g, "").slice(0, 14));
  };

  const send = async () => {
    if (!e164Ok) {
      toast.error(countryCode === "+1"
        ? "Enter a 10-digit phone number (e.g. 310-658-3787)."
        : "Enter a valid phone number for the selected country code.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/security/otp/send", {
        purpose: "phone_verify", phone_e164: e164,
      });
      setSentTo(r.data?.sent_to || e164);
      setStep("verify");
      toast.success(`Code sent to ${r.data?.sent_to || e164}`);
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  const confirm = async () => {
    if (code.length !== 6) { toast.error("Enter the 6-digit code"); return; }
    setBusy(true);
    try {
      await api.post("/security/otp/confirm", {
        purpose: "phone_verify", code, phone_e164: e164,
      });
      toast.success("Phone verified");
      reset();
      onSaved?.();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="surface p-6" data-testid="account-phone-panel">
      <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-1">
        <Phone className="w-3.5 h-3.5" /> Phone number
      </p>

      {verified && step !== "replacing" ? (
        <div className="mt-3">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-lg font-mono" data-testid="account-phone-value">{user.phone_e164}</p>
            <span className="inline-flex items-center gap-1 rounded-full bg-green-100 text-green-700 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest"
                  data-testid="account-phone-verified-chip">
              <CheckCircle2 className="w-3 h-3" /> Verified
            </span>
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            Text codes for email and password changes go here. Change the number below if you get a new phone.
          </p>
          <button onClick={() => { setStep("replacing"); setNational(""); }}
            data-testid="account-phone-change"
            className="mt-3 text-xs font-semibold text-primary hover:underline">
            Change phone number
          </button>
        </div>
      ) : step === "verify" ? (
        <div className="mt-3 grid gap-3">
          <div className="rounded-md border border-shell-border bg-shell-tint/40 px-3 py-2 text-xs">
            <p className="uppercase tracking-widest font-semibold text-muted-foreground">Code sent</p>
            <p className="mt-0.5">We texted a 6-digit code to <span className="font-mono">{sentTo}</span>. Expires in 5 minutes.</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1.5">6-digit code</p>
            <input
              data-testid="account-phone-otp"
              inputMode="numeric" maxLength={6} autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              placeholder="123456"
              className="w-40 rounded-md border border-shell-border bg-white px-3 py-2 text-lg font-mono tracking-widest focus:outline-none focus:border-primary"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={confirm} disabled={busy || code.length !== 6}
              data-testid="account-phone-confirm"
              className="btn-primary disabled:opacity-60">
              <ShieldCheck className="w-4 h-4" /> {busy ? "Verifying…" : "Verify phone"}
            </button>
            <button onClick={reset} disabled={busy} data-testid="account-phone-cancel"
              className="text-xs font-semibold text-muted-foreground hover:text-foreground border border-shell-border rounded-full px-4 py-2 inline-flex items-center gap-1">
              <RotateCcw className="w-3 h-3" /> Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-3 grid gap-3">
          {!verified && (
            <div className="rounded-lg border border-pink-200 bg-pink-50 px-3 py-2 text-xs text-pink-900"
                 data-testid="account-phone-notice">
              Verify a phone number to unlock email and password changes. We'll only text you for security codes.
            </div>
          )}
          <div>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1.5">
              {isTester ? "Phone number" : "Phone number (US)"}
            </p>
            <div className="flex gap-2 max-w-sm">
              {isTester ? (
                <select
                  data-testid="account-phone-country"
                  value={countryCode}
                  onChange={(e) => {
                    const cc = e.target.value;
                    setCountryCode(cc);
                    // Re-format the digits already typed under the new
                    // country's rules so switching from +1 → +91
                    // doesn't leave dashes in the field.
                    setNational((prev) => cc === "+1"
                      ? formatUsNational(prev)
                      : String(prev).replace(/\D/g, "").slice(0, 14));
                  }}
                  className="rounded-md border border-shell-border bg-white px-2 py-2 text-sm focus:outline-none focus:border-primary"
                >
                  {TESTER_COUNTRY_OPTIONS.map((o) => (
                    <option key={o.code} value={o.code}>{o.label}</option>
                  ))}
                </select>
              ) : null}
              <input
                data-testid="account-phone-input"
                value={national}
                onChange={(e) => onNationalChange(e.target.value)}
                placeholder={countryCode === "+1" ? "310-658-3787" : "98765 43210"}
                inputMode="numeric"
                type="tel"
                className="flex-1 rounded-md border border-shell-border bg-white px-3 py-2 text-sm font-mono focus:outline-none focus:border-primary"
              />
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">
              {isTester
                ? `We'll text a code to ${countryCode} ${nationalDigits || "your number"} to verify it — used for 2FA when you change your email or password.`
                : "10-digit US number. We'll text a code to verify it — used for 2FA when you change your email or password."}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={send} disabled={busy || !e164Ok}
              data-testid="account-phone-send-code"
              className="btn-primary disabled:opacity-60">
              {busy ? "Sending…" : "Send SMS code"} <ArrowRight className="w-4 h-4" />
            </button>
            {step === "replacing" && (
              <button onClick={reset} disabled={busy}
                data-testid="account-phone-cancel-change"
                className="text-xs font-semibold text-muted-foreground hover:text-foreground border border-shell-border rounded-full px-4 py-2 inline-flex items-center gap-1">
                <RotateCcw className="w-3 h-3" /> Cancel
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
